/**
 * Ficha de un punto: descripcion, video y pregunta cuando toca.
 *
 * La pregunta solo se pide al servidor si el cliente cree que se cumplen las
 * condiciones. Esto es una comodidad para el usuario, no una medida de
 * seguridad: quien decide de verdad es el servidor, que responde 403 con motivo
 * si no se cumplen. Aqui se evita ensenar un boton que iba a fallar.
 */

import { useTranslation } from 'react-i18next';
import { formatearDistancia, type PuntoPublico } from '@recorregut/shared';
import { usePregunta } from '@/api/consultas.ts';
import { etiquetaFormato } from '@/i18n';
import { ErrorRespuesta } from '@/api/cliente.ts';
import { MensajeError } from './MensajeError.tsx';
import { Pregunta } from './Pregunta.tsx';
import { ReproductorVideo } from './ReproductorVideo.tsx';

interface Props {
  punto: PuntoPublico;
  /** Primera visita confirmada por el servidor: el video arranca solo. */
  reproduccionAutomatica: boolean;
  visitado: boolean;
  eventoEnCurso: boolean;
  perteneceAlEvento: boolean;
  distancia: number | undefined;
}

export function FichaPunto({
  punto,
  reproduccionAutomatica,
  visitado,
  eventoEnCurso,
  perteneceAlEvento,
  distancia,
}: Props) {
  const { t } = useTranslation();
  const formato = etiquetaFormato();

  const puedeHaberPregunta = visitado && eventoEnCurso && perteneceAlEvento;
  const consultaPregunta = usePregunta(punto.id, puedeHaberPregunta);

  return (
    <div className="space-y-6">
      {punto.descripcion ? (
        <p className="text-base leading-relaxed text-tinta-700">{punto.descripcion}</p>
      ) : null}

      {distancia !== undefined && !visitado ? (
        <p className="aviso">
          <span className="font-semibold">
            {t('punt.distancia', { distancia: formatearDistancia(distancia, formato) })}
          </span>
          {'. '}
          {t('punt.acostarse', {
            radi: formatearDistancia(punto.radio_metros, formato),
          })}
        </p>
      ) : null}

      <section aria-labelledby="titulo-video">
        <h3 id="titulo-video" className="antetitulo mb-3">
          {t('video.titol')}
        </h3>
        <ReproductorVideo punto={punto} automatico={reproduccionAutomatica} />
      </section>

      {puedeHaberPregunta ? (
        <section aria-labelledby="titulo-pregunta">
          <h3 id="titulo-pregunta" className="antetitulo mb-3">
            {t('pregunta.titol')}
          </h3>

          {consultaPregunta.isPending ? (
            <p className="text-sm text-tinta-500">{t('app.carregant')}</p>
          ) : consultaPregunta.isError ? (
            // Un 403 aqui no es una averia: es el servidor diciendo que aun no
            // toca. Se muestra el motivo traducido, sin boton de reintento.
            <MensajeError
              error={consultaPregunta.error}
              {...(consultaPregunta.error instanceof ErrorRespuesta &&
              consultaPregunta.error.estado >= 500
                ? { alReintentar: () => void consultaPregunta.refetch() }
                : {})}
            />
          ) : consultaPregunta.data ? (
            <Pregunta pregunta={consultaPregunta.data} />
          ) : null}
        </section>
      ) : perteneceAlEvento && !visitado ? (
        <p className="tarjeta-hundida text-sm text-tinta-700">{t('pregunta.calVisitar')}</p>
      ) : perteneceAlEvento && !eventoEnCurso ? (
        <p className="tarjeta-hundida text-sm text-tinta-700">{t('pregunta.foraDeFranja')}</p>
      ) : null}
    </div>
  );
}
